import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  Auth,
  User,
  initializeAuth,
  browserLocalPersistence, // Or other persistence as needed
} from 'firebase/auth';
// Assuming you might need Firestore later, keep the import structure similar
// import { getFirestore, Firestore } from 'firebase/firestore';
import { environment } from 'src/environments/environment'; // Assuming you have firebaseConfig here

class FirebaseAuthBackend {
  auth: Auth;
  // firestore: Firestore; // If you use Firestore

  constructor() {
    if (environment.firebaseConfig) {
      // Initialize Firebase
      const app = initializeApp(environment.firebaseConfig);

      // Use initializeAuth for better control, especially in SSR contexts if needed
      // Using browserLocalPersistence for web standard behavior
      this.auth = initializeAuth(app, {
        persistence: browserLocalPersistence,
      });

      // this.firestore = getFirestore(app); // Initialize Firestore if needed

      onAuthStateChanged(this.auth, (user) => {
        if (user) {
          sessionStorage.setItem('authUser', JSON.stringify(user));
        } else {
          sessionStorage.removeItem('authUser');
        }
      });
    } else {
      console.error('Firebase config not found in environment.');
      // Handle the case where config is missing, maybe throw an error
      // For now, assign a default null/undefined or handle appropriately
      this.auth = null; // Or throw an error
    }
  }

  /**
   * Registers the user with given details
   */
  registerUser = (email, password): Promise<User> => {
    return new Promise((resolve, reject) => {
      createUserWithEmailAndPassword(this.auth, email, password)
        .then((userCredential) => {
          resolve(userCredential.user);
        })
        .catch((error) => {
          reject(this._handleError(error));
        });
    });
  };

  /**
   * Login user with given details
   */
  loginUser = (email, password): Promise<User> => {
    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(this.auth, email, password)
        .then((userCredential) => {
          resolve(userCredential.user);
        })
        .catch((error) => {
          reject(this._handleError(error));
        });
    });
  };

  /**
   * forget Password user with given details
   */
  forgetPassword = (email): Promise<void> => {
    return new Promise((resolve, reject) => {
      sendPasswordResetEmail(this.auth, email, {
        url: window.location.protocol + '//' + window.location.host + '/login', // Or your specific action URL
      })
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(this._handleError(error));
        });
    });
  };

  /**
   * Logout the user
   */
  logout = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      signOut(this.auth)
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(this._handleError(error));
        });
    });
  };

  setLoggeedInUser = (user: User | object) => {
    // Consider storing only necessary, non-sensitive user info if stringifying the whole User object
    sessionStorage.setItem('authUser', JSON.stringify(user));
  };

  /**
   * Returns the authenticated user from session storage
   */
  getAuthenticatedUser = (): User | null => {
    const userJson = sessionStorage.getItem('authUser');
    if (!userJson) {
      return null;
    }
    try {
      // Parse carefully, might not be a full User object depending on what was stored
      return JSON.parse(userJson) as User; // Cast might need adjustment
    } catch (e) {
      console.error('Error parsing authUser from session storage', e);
      sessionStorage.removeItem('authUser'); // Clear invalid data
      return null;
    }
  };

  /**
   * Handle the error
   * @param {*} error
   */
  _handleError(error: any): string { // Type the error if possible (e.g., FirebaseError)
    // Firebase v9 errors often have a 'code' property (e.g., 'auth/user-not-found')
    const errorMessage = error.message || 'An unknown error occurred';
    console.error("Firebase Error:", error.code, errorMessage); // Log the code too
    return errorMessage; // Consider returning more structured error info if needed
  }
}

let _fireBaseBackend: FirebaseAuthBackend | null = null;

/**
 * Initilize the backend
 */
const initFirebaseBackend = (): FirebaseAuthBackend => {
  if (!_fireBaseBackend) {
    _fireBaseBackend = new FirebaseAuthBackend();
  }
  return _fireBaseBackend;
};

/**
 * Returns the firebase backend
 */
const getFirebaseBackend = (): FirebaseAuthBackend => {
  if (!_fireBaseBackend) {
     // Initialize if not already done, or throw error
     console.warn("Firebase backend requested before initialization.");
     initFirebaseBackend();
  }
  return _fireBaseBackend;
};

export { initFirebaseBackend, getFirebaseBackend };